import { useCallback } from "react";
import { Reference, useApolloClient } from "@apollo/client";
import { useAuth } from "modules/providers/auth-provider";
import { IFileInTransit } from "modules/providers/index-db-provider/index-db-models";
import moment from "moment";
import {
  MessageThreadFragment,
  SearchFilter,
  SearchFilterType,
  MessageInput,
  QuotedMessageFragment,
  FileFragment,
  GifFragment,
  MessageType,
  MessageFragment,
  UserFragment,
  MessageThreadSortType,
  MessageThreadSortKeysFragment,
} from "gql/graphql";
import { FILE_THUMB_CONTENT_TYPE } from "modules/utils/Constants";
import ObjectID from "bson-objectid";
import useQueryString from "./use-query-string";
import { messageThreadFindQueryRegistry } from "app/message-thread-find-registry";
import { ReadFieldFunction } from "@apollo/client/cache/core/types/common";

const useMessageThreadCache = () => {
  const { cache } = useApolloClient();
  const { me } = useAuth();
  const { qsMessageThreadId } = useQueryString();

  const isDistributionGroupIdListMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (
        !searchFilter.objectIdListValue ||
        searchFilter.objectIdListValue.length === 0
      ) {
        return true;
      }
      const searchFilterObjectIdList = searchFilter.objectIdListValue;
      return searchFilterObjectIdList.every((distributionGroupId) =>
        messageThread.distributionGroupIdList.includes(distributionGroupId),
      );
    },
    [],
  );

  const isTagIdListMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (
        !searchFilter.objectIdListValue ||
        searchFilter.objectIdListValue.length === 0
      ) {
        return true;
      }
      const searchFilterObjectIdList = searchFilter.objectIdListValue;
      return searchFilterObjectIdList.every((tagId) =>
        messageThread.tagIdList.includes(tagId),
      );
    },
    [],
  );

  const isUserIdListMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (
        !searchFilter.objectIdListValue ||
        searchFilter.objectIdListValue.length === 0
      ) {
        return true;
      }
      const searchFilterObjectIdList = searchFilter.objectIdListValue;
      return searchFilterObjectIdList.every((userId) =>
        messageThread.searchUserIdList.includes(userId),
      );
    },
    [],
  );

  const isHasAttachmentsMatch = useCallback(
    (messageThread: MessageThreadFragment) => messageThread.fileCount > 0,
    [],
  );

  const isHasAccessMatch = useCallback(
    (messageThread: MessageThreadFragment) => {
      if (!me) return false;
      return messageThread.accessUserIdList.includes(me.id);
    },
    [me],
  );

  const isIsCalledOutMatch = useCallback(
    (messageThread: MessageThreadFragment) => {
      if (!me) return false;
      return messageThread.calloutUserIdList.includes(me.id);
    },
    [me],
  );

  const isTypeMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) =>
      searchFilter.stringListValue?.includes(messageThread.type),
    [],
  );

  const isIsInboxMatch = useCallback(
    (messageThread: MessageThreadFragment) => messageThread.isInbox,
    [],
  );

  const isIsNotInboxMatch = useCallback(
    (messageThread: MessageThreadFragment) => !messageThread.isInbox,
    [],
  );

  const isIsLockedMatch = useCallback(
    (messageThread: MessageThreadFragment) => messageThread.isLocked,
    [],
  );

  const isIsNotPinnedByMeMatch = useCallback(
    (messageThread: MessageThreadFragment) => !messageThread.isPinned,
    [],
  );

  const isIsPinnedByMeMatch = useCallback(
    (messageThread: MessageThreadFragment) => messageThread.isPinned,
    [],
  );

  const isIsNotSeenByMeMatch = useCallback(
    (messageThread: MessageThreadFragment) => !messageThread.isSeen,
    [],
  );

  const isIsReminderMatch = useCallback(
    (messageThread: MessageThreadFragment) => !!messageThread.remindTime,
    [],
  );

  const isIsSentByMeMatch = useCallback(
    (messageThread: MessageThreadFragment) => {
      if (!me) return false;
      return messageThread.createdUserId === me.id;
    },
    [me],
  );

  const isStartDateMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) =>
      moment(messageThread.sortTime).isSameOrAfter(searchFilter.instantValue),
    [],
  );

  const isEndDateMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) =>
      moment(messageThread.createTime).isSameOrBefore(
        searchFilter.instantValue,
      ),
    [],
  );

  const isIsStarredByMe = useCallback(
    (messageThread: MessageThreadFragment) => messageThread.isStarred,
    [],
  );

  const isCreateMessageMatch = useCallback(
    (messageThread: MessageThreadFragment) => messageThread.isCreateMessage,
    [],
  );

  const isCreatedUserIdListMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) =>
      searchFilter.objectIdListValue?.includes(messageThread.createdUserId),
    [],
  );

  const isEmailUserIdListMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) =>
      !!searchFilter.objectIdListValue?.some((filterId) =>
        messageThread.emailUserIdList?.includes(filterId),
      ),
    [],
  );

  const isReadOnlyMatch = useCallback(
    (messageThread: MessageThreadFragment) => {
      if (!me) return false;
      return !messageThread.createMessageUserIdList.includes(me.id);
    },
    [me],
  );

  const isTaskAssigneeUserIdListMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (
        !messageThread.task ||
        !messageThread.task.assignedUserId ||
        !searchFilter.objectIdListValue
      ) {
        return false;
      }
      return searchFilter.objectIdListValue.includes(
        messageThread.task.assignedUserId,
      );
    },
    [],
  );

  const isTaskCompleteStartDateMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (!messageThread.task || !messageThread.task.completeTime) {
        return false;
      }
      return moment(messageThread.task.completeTime).isSameOrAfter(
        searchFilter.instantValue,
      );
    },
    [],
  );

  const isTaskCompleteEndDateMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (!messageThread.task || !messageThread.task.completeTime) {
        return false;
      }
      return moment(messageThread.task.completeTime).isSameOrBefore(
        searchFilter.instantValue,
      );
    },
    [],
  );

  const isTaskDueStartDateMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (!messageThread.task || !messageThread.task.dueTime) {
        return false;
      }
      return moment(messageThread.task.dueTime).isSameOrAfter(
        searchFilter.instantValue,
      );
    },
    [],
  );

  const isTaskDueEndDateMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (!messageThread.task || !messageThread.task.dueTime) {
        return false;
      }
      return moment(messageThread.task.dueTime).isSameOrBefore(
        searchFilter.instantValue,
      );
    },
    [],
  );

  const isTaskPriorityTypeListMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (!messageThread.task || !messageThread.task.priorityType) {
        return false;
      }
      return searchFilter.stringListValue?.includes(
        messageThread.task.priorityType,
      );
    },
    [],
  );

  const isTaskStatusTypeListMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilter: SearchFilter) => {
      if (!messageThread.task || !messageThread.task.statusType) {
        return false;
      }
      return searchFilter.stringListValue?.includes(
        messageThread.task.statusType,
      );
    },
    [],
  );

  const isMessageThreadFiltersMatch = useCallback(
    (messageThread: MessageThreadFragment, searchFilters: SearchFilter[]) =>
      searchFilters.every((searchFilter) => {
        switch (searchFilter.type) {
          case SearchFilterType.DistributionGroupIdList:
            return isDistributionGroupIdListMatch(messageThread, searchFilter);
          case SearchFilterType.TagIdList:
            return isTagIdListMatch(messageThread, searchFilter);
          case SearchFilterType.UserIdList:
            return isUserIdListMatch(messageThread, searchFilter);
          case SearchFilterType.HasAttachments:
            return isHasAttachmentsMatch(messageThread);
          case SearchFilterType.HasAccess:
            return isHasAccessMatch(messageThread);
          case SearchFilterType.IsCalledOut:
            return isIsCalledOutMatch(messageThread);
          case SearchFilterType.TypeList:
            return isTypeMatch(messageThread, searchFilter);
          case SearchFilterType.IsInbox:
            return isIsInboxMatch(messageThread);
          case SearchFilterType.IsLocked:
            return isIsLockedMatch(messageThread);
          case SearchFilterType.IsNotInbox:
            return isIsNotInboxMatch(messageThread);
          case SearchFilterType.IsNotPinnedByMe:
            return isIsNotPinnedByMeMatch(messageThread);
          case SearchFilterType.IsPinnedByMe:
            return isIsPinnedByMeMatch(messageThread);
          case SearchFilterType.IsNotSeenByMe:
            return isIsNotSeenByMeMatch(messageThread);
          case SearchFilterType.IsSentByMe:
            return isIsSentByMeMatch(messageThread);
          case SearchFilterType.IsReminder:
            return isIsReminderMatch(messageThread);
          case SearchFilterType.IsStarredByMe:
            return isIsStarredByMe(messageThread);
          case SearchFilterType.StartDate:
            return isStartDateMatch(messageThread, searchFilter);
          case SearchFilterType.EndDate:
            return isEndDateMatch(messageThread, searchFilter);
          case SearchFilterType.CanCreateMessage:
            return isCreateMessageMatch(messageThread);
          case SearchFilterType.CreatedUserIdList:
            return isCreatedUserIdListMatch(messageThread, searchFilter);
          case SearchFilterType.EmailUserIdList:
            return isEmailUserIdListMatch(messageThread, searchFilter);
          case SearchFilterType.IsReadOnly:
            return isReadOnlyMatch(messageThread);
          case SearchFilterType.TaskAssigneeUserIdList:
            return isTaskAssigneeUserIdListMatch(messageThread, searchFilter);
          case SearchFilterType.TaskCompleteStartDate:
            return isTaskCompleteStartDateMatch(messageThread, searchFilter);
          case SearchFilterType.TaskCompleteEndDate:
            return isTaskCompleteEndDateMatch(messageThread, searchFilter);
          case SearchFilterType.TaskDueStartDate:
            return isTaskDueStartDateMatch(messageThread, searchFilter);
          case SearchFilterType.TaskDueEndDate:
            return isTaskDueEndDateMatch(messageThread, searchFilter);
          case SearchFilterType.TaskPriorityTypeList:
            return isTaskPriorityTypeListMatch(messageThread, searchFilter);
          case SearchFilterType.TaskStatusTypeList:
            return isTaskStatusTypeListMatch(messageThread, searchFilter);
          default:
            return true;
        }
      }),
    [
      isTaskStatusTypeListMatch,
      isTaskPriorityTypeListMatch,
      isTaskDueStartDateMatch,
      isTaskDueEndDateMatch,
      isTaskCompleteStartDateMatch,
      isTaskCompleteEndDateMatch,
      isTaskAssigneeUserIdListMatch,
      isReadOnlyMatch,
      isDistributionGroupIdListMatch,
      isTagIdListMatch,
      isUserIdListMatch,
      isHasAttachmentsMatch,
      isHasAccessMatch,
      isIsCalledOutMatch,
      isTypeMatch,
      isIsInboxMatch,
      isIsLockedMatch,
      isIsNotInboxMatch,
      isIsNotPinnedByMeMatch,
      isIsPinnedByMeMatch,
      isIsNotSeenByMeMatch,
      isIsSentByMeMatch,
      isIsReminderMatch,
      isIsStarredByMe,
      isStartDateMatch,
      isEndDateMatch,
      isCreateMessageMatch,
      isCreatedUserIdListMatch,
      isEmailUserIdListMatch,
    ],
  );

  const compareSortKeys = useCallback(
    (aSortKey?: string | null, bSortKey?: string | null) => {
      if (!aSortKey && !bSortKey) return 0;
      if (!aSortKey) return 1;
      if (!bSortKey) return -1;
      return aSortKey.localeCompare(bSortKey);
    },
    [],
  );

  const compareMessageThread = useCallback(
    (
      a: Reference,
      b: Reference,
      sortType: MessageThreadSortType,
      readField: ReadFieldFunction,
    ): number => {
      const aSort = readField<MessageThreadSortKeysFragment>("sortKeys", a);
      const bSort = readField<MessageThreadSortKeysFragment>("sortKeys", b);
      if (!aSort && !bSort) return 0;
      if (!aSort) return 1;
      if (!bSort) return -1;
      switch (sortType) {
        case MessageThreadSortType.SortTime: {
          return aSort.defaultSortKey.localeCompare(bSort.defaultSortKey);
        }
        case MessageThreadSortType.TaskDueTime: {
          return compareSortKeys(aSort.taskDueSortKey, bSort.taskDueSortKey);
        }
        case MessageThreadSortType.TaskCompleteTime: {
          return compareSortKeys(
            aSort.taskCompleteSortKey,
            bSort.taskCompleteSortKey,
          );
        }
        default:
          return 0;
      }
    },
    [compareSortKeys],
  );

  const insertSorted = useCallback(
    (
      list: Reference[],
      ref: Reference,
      sortType: MessageThreadSortType,
      readField: ReadFieldFunction,
    ) => {
      const next = [...list, ref];

      next.sort((a, b) => compareMessageThread(a, b, sortType, readField));

      return next;
    },
    [compareMessageThread],
  );

  const updateMessageThreadQueryCache = useCallback(
    (messageThread: MessageThreadFragment) => {
      for (const entry of messageThreadFindQueryRegistry.getAll()) {
        const { variables, cacheKey } = entry;

        const messageThreadIsMatch = isMessageThreadFiltersMatch(
          messageThread,
          variables.input.searchFilterList,
        );

        const sortType = variables.input.sortType;

        cache.modify({
          id: "ROOT_QUERY",
          fields: {
            [cacheKey](existing, { readField, toReference }) {
              const list: Reference[] = Array.isArray(existing) ? existing : [];
              const messageThreadInList = list.some(
                (ref) => readField("id", ref) === messageThread.id,
              );

              //remove from list
              if (!messageThreadIsMatch) {
                if (qsMessageThreadId === messageThread.id) {
                  return list;
                }
                return messageThreadInList
                  ? list.filter(
                      (ref) => readField("id", ref) !== messageThread.id,
                    )
                  : list;
              }

              //insert into list
              const listWithoutMessageThread = messageThreadInList
                ? list.filter(
                    (ref) => readField("id", ref) !== messageThread.id,
                  )
                : list;

              const ref = toReference(messageThread);

              if (!ref) return listWithoutMessageThread;

              return insertSorted(
                listWithoutMessageThread,
                ref,
                sortType,
                readField,
              );
            },
          },
        });
      }
    },
    [cache, insertSorted, isMessageThreadFiltersMatch, qsMessageThreadId],
  );

  const getOptimisticFile = useCallback(
    (
      me: UserFragment,
      messageThreadId: string,
      file: IFileInTransit,
    ): FileFragment => ({
      __typename: "File",
      id: file.id,
      userId: me.id,
      name: file.name,
      previewCloudFrontUrl: file.previewCloudFrontUrl,
      cloudFrontUrl: file.url,
      messageThreadId,
      contentType: file.contentType,
      createTime: moment().toISOString(),
      byteCount: file.byteCount,
      isVoiceNote: file.isVoiceNote,
      voiceNoteLocalizedText: null,
      voiceNoteLocale: null,
      messageId: null,
      voiceNoteStatus: null,
      voiceNoteText: null,
      key: file.key,
      previewKey: file.previewKey,
      isCompressed: !!file.isCompressed,
      previewContentType: FILE_THUMB_CONTENT_TYPE,
    }),
    [],
  );

  const getOptimisticMessage = useCallback(
    (
      messageThreadId: string,
      messageInput: MessageInput,
      files?: IFileInTransit[],
      quotedMessageList?: QuotedMessageFragment[],
    ) => {
      if (!me) return null;

      const optimisticFiles = (files || []).map(
        (file): FileFragment => getOptimisticFile(me, messageThreadId, file),
      );

      const optimisticQuotedMessages = quotedMessageList?.map(
        (quotedMessage): QuotedMessageFragment => ({
          ...quotedMessage,
          __typename: "QuotedMessage",
          id: quotedMessage.id,
        }),
      );

      const optimisticGifs = messageInput.gifList.map(
        (gif): GifFragment => ({
          ...gif,
          __typename: "Gif",
          id: new ObjectID().toHexString(),
          videoUrl: gif.videoUrl ?? null,
          imageUrl: gif.imageUrl,
          url: gif.url ?? null,
          name: gif.name ?? null,
          height: gif.height ?? null,
          width: gif.width ?? null,
        }),
      );

      const messageFragment: MessageFragment = {
        __typename: "Message",
        id: messageInput.id ?? new ObjectID().toHexString(),
        messageThreadId,
        createTime: messageInput.sentTime,
        createdUserId: me.id,
        type: MessageType.User,
        subType: null,
        editTime: null,
        updateTime: messageInput.sentTime,
        deleted: false,
        isSeen: true,
        fileList: optimisticFiles,
        reactionList: [],
        actionText: "",
        quillDelta: messageInput.quillDelta || null,
        gifList: optimisticGifs ?? [],
        quotedMessageList: optimisticQuotedMessages ?? [],
        locale: null,
        localizedText: null,
        seenUserIdList: [me.id],
        relatedMessageThreadList: [],
        sentTime: messageInput.sentTime,
        text: messageInput.text,
      };

      return messageFragment;
    },
    [getOptimisticFile, me],
  );

  return {
    getOptimisticMessage,
    getOptimisticFile,
    updateMessageThreadQueryCache,
    isMessageThreadFiltersMatch,
  };
};

export default useMessageThreadCache;
